import { computed, inject, Injectable, NgZone, signal } from '@angular/core';

import { WINDOW } from '@core/window.token';
import { ENV } from '@core/env.token';
import { LogService } from '@core/log.service';

type OsanoConsentValue = 'ACCEPT' | 'DENY';

export interface OsanoConsentObject {
  ANALYTICS: OsanoConsentValue;
  MARKETING: OsanoConsentValue;
  PERSONALIZATION: OsanoConsentValue;
  ESSENTIAL: OsanoConsentValue;
}

export interface ConsentState {
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
}

interface OsanoConsentManager {
  locale: string;
  readonly analytics: boolean;
  readonly marketing: boolean;
  readonly personalization: boolean;
  addEventListener(event: string, callback: (...args: unknown[]) => void): void;
  showDrawer(): void;
  getConsent(): OsanoConsentObject;
  mode: string;
  hideDialog(): void;
}

interface OsanoPreload {
  (...args: unknown[]): void;
  data?: unknown[];
  cm?: OsanoConsentManager;
}

interface OsanoWindow extends Window {
  Osano?: OsanoPreload;
}

const DEFAULT_CONSENT: ConsentState = { analytics: true, marketing: true, personalization: true };

/**
 * Wraps the Osano CMP consent state and exposes it as Angular signals.
 *
 * Call {@link initialize} once at app startup to inject the Osano script and
 * register event listeners. Individual `analytics`, `marketing`, and
 * `personalization` computed signals derive from a single consent signal so
 * consumers only re-render when the category they depend on actually changes.
 *
 * When Osano is not configured or not loaded (e.g. blocked by an ad-blocker,
 * or absent in a test/local environment) all signals default to `false`.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  private readonly window = inject(WINDOW) as OsanoWindow;
  private readonly ngZone = inject(NgZone);
  private readonly env = inject(ENV);
  private readonly logger = inject(LogService);

  private readonly _consent = signal<ConsentState>(DEFAULT_CONSENT);

  private hiddenPathPrefixes: string[] = [];

  /** Full consent state object. */
  readonly consent = this._consent.asReadonly();

  /** Computed convenience signals — only notify when their value changes. */
  readonly analytics = computed(() => this._consent().analytics);
  readonly marketing = computed(() => this._consent().marketing);
  readonly personalization = computed(() => this._consent().personalization);

  /**
   * Injects the Osano script into the document and wires up consent listeners.
   *
   * This must be called as the **first** app initializer so the script is
   * present before any other code checks consent state.
   *
   * Resolves once the Osano CMP has initialized (or immediately if no URL is
   * configured).
   */
  initialize(): Promise<void> {
    const url = this.env.osanoScriptUrl;
    if (!url) {
      return Promise.resolve();
    }

    this.hiddenPathPrefixes = [this.env.authHubOrigin];

    return new Promise<void>((resolve) => {
      // Install the Osano pre-load stub so we can register callbacks before
      // the external script finishes loading.
      this.installPreloadStub();

      const osano = this.window.Osano;
      // Register the onInitialized callback *before* appending the script so
      // it fires even if the script loads instantly (e.g. from cache).
      // DOES NOT fire when CMP is in discovery mode.
      if (osano) {
        osano('onInitialized', () => {
          this.logger.debug('Osano CMP initialized');
          this.setLocale();
          this.seedFromCm();
          this.listenForChanges();
          this.hideDialogIfOnHiddenRoute();
          resolve();
        });

        osano('onUiChanged', (component: unknown, stateChange: unknown) => {
          if (component === 'dialog' && stateChange === 'show') {
            this.hideDialogIfOnHiddenRoute();
          }
        });
      }

      this.appendScript(url, resolve);

      // SHIP-8805: Avoid href clicks to trigger the whole page to reload.
      // This is a known Angular Router issue -
      // the router intercepts all <a> tag clicks, including Osano's internal href="#fragment" links, and treats them as route navigations.
      this.window.document.addEventListener(
        'click',
        (e: Event) => {
          const target = e.target as HTMLElement;
          if (
            target.tagName === 'A' &&
            target.getAttribute('href')?.startsWith('#') &&
            target.closest('.osano-cm-drawer, .osano-cm-dialog')
          ) {
            e.preventDefault();
          }
        },
        true
      );
    });
  }

  /**
   * Opens the Osano consent-preferences drawer.
   * Does nothing in discovery mode.
   */
  showDrawer(): void {
    this.window.Osano?.cm?.showDrawer();
  }

  hideDialog(): void {
    this.window.Osano?.cm?.hideDialog();
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Creates the `window.Osano()` pre-load queue function exactly as documented
   * by Osano, so callbacks registered before the script loads are replayed
   * once it initializes.
   */
  private installPreloadStub(): void {
    const w = this.window as unknown as Record<string, unknown>;
    const osano: OsanoPreload = function (this: OsanoPreload) {
      osano.data ??= [];
      // eslint-disable-next-line prefer-rest-params
      osano.data.push(arguments);
    };

    osano.data = osano.data || [];
    w['Osano'] = osano;
  }

  /** Appends the Osano `<script>` to `<head>`. */
  private appendScript(url: string, resolve: () => void): void {
    const doc = this.window.document;
    const script = doc.createElement('script');
    script.src = url;
    script.async = false;
    script.onload = () => {
      if (this.window.Osano?.cm?.mode === 'debug') {
        this.logger.warn('Osano CMP in discovery mode; Discovery/Listening only.');
        resolve();
      }
    };
    doc.head.appendChild(script);
  }

  /** Sets the locale for the Osano CMP. */
  private setLocale(): void {
    const cm = this.window.Osano?.cm;
    if (!cm) {
      return;
    }

    cm.locale = 'en';
  }

  /** Reads the current consent state from `Osano.cm` properties. */
  private seedFromCm(): void {
    const cm = this.window.Osano?.cm;
    if (!cm) {
      return;
    }
    this._consent.set({
      analytics: cm.analytics === true,
      marketing: cm.marketing === true,
      personalization: cm.personalization === true,
    });
  }

  /** Subscribes to `osano-cm-consent-saved` to keep signals in sync. */
  private listenForChanges(): void {
    const cm = this.window.Osano?.cm;
    if (!cm) {
      return;
    }
    cm.addEventListener('osano-cm-consent-saved', (...args: unknown[]) => {
      const consent = args[0] as OsanoConsentObject;
      this.ngZone.run(() => {
        this._consent.set({
          analytics: consent.ANALYTICS === 'ACCEPT',
          marketing: consent.MARKETING === 'ACCEPT',
          personalization: consent.PERSONALIZATION === 'ACCEPT',
        });
      });
    });
  }

  private hideDialogIfOnHiddenRoute(): void {
    const currentPath = this.window.location.href;
    const shouldHide = this.hiddenPathPrefixes.some((r) => currentPath.startsWith(r));
    if (shouldHide) {
      this.hideDialog();
    }
  }
}
